import React, { useEffect, useState, useRef } from 'react';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import { useAudio } from '../context/AudioContext';

// Componente base para elementos de jogo
const GameElementsContainer = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  pointer-events: none;
  align-items: center;
  justify-content: center;
  display: flex;
  z-index: 10;
  overflow: hidden;
`;

const ScoreDisplay = styled(motion.div)`
  position: fixed;
  top: 20px;
  align-items: center;
  justify-content: center;
  right: 20px;
  background: rgba(26, 26, 26, 0.7);
  border-radius: 10px;
  padding: 0.5rem 1rem;
  color: var(--color-white);
  font-size: 1rem;
  border: 1px solid var(--color-primary);
  box-shadow: 0 0 10px rgba(0, 0, 0, 0.3);
  backdrop-filter: blur(5px);
  z-index: 100;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  
  @media (max-width: 768px) {
    padding: 0.7rem 1.2rem;
    font-size: 1.2rem;
    top: 10px;
    right: 10px;
  }
`;

const AchievementNotification = styled(motion.div)`
  position: fixed;
  top: 80px;
  right: 20px;
  background: rgba(26, 26, 26, 0.9);
  border-radius: 10px;
  padding: 1rem;
  color: var(--color-white);
  font-size: 1rem;
  border: 1px solid var(--color-accent);
  box-shadow: 0 0 20px rgba(255, 187, 51, 0.3);
  backdrop-filter: blur(10px);
  z-index: 100;
  max-width: 300px;
  
  @media (max-width: 768px) {
    top: 70px;
    right: 10px;
    max-width: 250px;
    padding: 0.8rem;
  }
`;

const AchievementTitle = styled.h3`
  font-size: 1.2rem;
  margin: 0 0 0.5rem;
  color: var(--color-accent);
  
  @media (max-width: 768px) {
    font-size: 1.1rem;
  }
`;

const AchievementDescription = styled.p`
  font-size: 0.9rem;
  margin: 0;
  opacity: 0.8;
  
  @media (max-width: 768px) {
    font-size: 0.85rem;
  }
`;

// Item colecionável
const CollectibleItem = styled(motion.div)<{ top: number; left: number }>`
  position: absolute;
  top: ${props => props.top}%;
  left: ${props => props.left}%;
  font-size: 1.8rem;
  cursor: pointer;
  pointer-events: auto;
  filter: drop-shadow(0 0 8px rgba(255, 255, 255, 0.7));
  z-index: 15;
  user-select: none;
  
  &:hover {
    transform: scale(1.2);
  }
  
  @media (max-width: 768px) {
    font-size: 1.5rem;
  }
`;

// Tipos de itens colecionáveis
const collectibleTypes = ['💎', '🔮', '⭐', '🏆', '💡', '🎮', '🚀', '🔍'];

// Interface para os itens colecionáveis
interface Collectible {
  id: string;
  type: string;
  top: number;
  left: number;
  points: number;
}

// Componente principal - REINTRODUZINDO ITENS COLECIONÁVEIS COM FREQUÊNCIA REDUZIDA
const GameElements: React.FC = () => {
  const [score, setScore] = useState(0);
  const [achievement, setAchievement] = useState<{ title: string; description: string } | null>(null);
  const [collectibles, setCollectibles] = useState<Collectible[]>([]);
  const collectiblesRef = useRef<Collectible[]>([]);

  const { playSfx } = useAudio();
  
  // Configurações para tornar os itens mais raros
  const MAX_COLLECTIBLES = 1; // Apenas 1 item visível por vez
  const MIN_SPAWN_INTERVAL = 30000; // Mínimo de 30 segundos entre spawns
  const MAX_SPAWN_INTERVAL = 90000; // Máximo de 90 segundos entre spawns
  
  // Atualizar a referência quando o estado mudar
  useEffect(() => {
    collectiblesRef.current = collectibles;
  }, [collectibles]);
  
  // Iniciar o sistema de spawn de itens colecionáveis
  useEffect(() => {
    const spawnCollectible = () => {
      // Se já atingiu o limite máximo, não spawna mais
      if (collectiblesRef.current.length >= MAX_COLLECTIBLES) {
        return;
      }
      
      // Gerar posição aleatória (evitando as bordas)
      const top = Math.random() * 70 + 15; // Entre 15% e 85% da altura
      const left = Math.random() * 70 + 15; // Entre 15% e 85% da largura
      
      // Selecionar tipo aleatório
      const type = collectibleTypes[Math.floor(Math.random() * collectibleTypes.length)];
      
      // Pontos baseados na raridade (simulada)
      const points = Math.floor(Math.random() * 15) + 5; // Entre 5 e 20 pontos
      
      // Criar novo item
      const newCollectible: Collectible = {
        id: `collectible-${Date.now()}`,
        type,
        top,
        left,
        points
      };
      
      // Adicionar ao estado
      setCollectibles(prev => [...prev, newCollectible]);
      
      // Agendar próximo spawn com intervalo aleatório
      const nextInterval = Math.random() * (MAX_SPAWN_INTERVAL - MIN_SPAWN_INTERVAL) + MIN_SPAWN_INTERVAL;
      setTimeout(spawnCollectible, nextInterval);
    };
    
    // Iniciar com um pequeno atraso para não aparecer imediatamente na carga da página
    const initialDelay = Math.random() * 10000 + 5000; // Entre 5 e 15 segundos
    const initialTimer = setTimeout(spawnCollectible, initialDelay);
    
    // Limpar timeout ao desmontar
    return () => clearTimeout(initialTimer);
  }, []);
  
  // Coletar item
  const collectItem = (item: Collectible) => {
    // Remover do estado
    setCollectibles(prev => prev.filter(c => c.id !== item.id));
    
    // Adicionar pontos
    const newScore = score + item.points;
    setScore(newScore);
    
    // Verificar conquistas
    checkAchievements(newScore);
    // tocar som de coleta
    playSfx('/audio/sfx.mp3');
  };
  
  // Verificar conquistas
  const checkAchievements = (currentScore: number) => {
    if (currentScore >= 50 && currentScore < 100) {
      showAchievement('Beginner', 'You collected 50 points! Keep exploring to unlock more achievements.');
    } else if (currentScore >= 100 && currentScore < 200) {
      showAchievement('Experienced Collector', 'You collected 100 points! Your dedication is impressive.');
    } else if (currentScore >= 200) {
      showAchievement('Master Collector', 'You collected 200 points! You are a true master at finding treasures.');
    }
  };
  
  // Mostrar notificação de conquista
  const showAchievement = (title: string, description: string) => {
    setAchievement({ title, description });
    
    // Esconder após alguns segundos
    setTimeout(() => {
      setAchievement(null);
    }, 5000);
  };

  return (
    <>
      <GameElementsContainer>
        <AnimatePresence>
          {collectibles.map(item => (
            <CollectibleItem
              key={item.id}
              top={item.top}
              left={item.left}
              initial={{ opacity: 0, scale: 0 }}
              animate={{ 
                opacity: 1, 
                scale: [1, 1.1, 1],
                y: [0, -5, 0, 5, 0]
              }}
              exit={{ opacity: 0, scale: 0 }}
              transition={{ 
                duration: 0.5,
                y: {
                  repeat: Infinity,
                  duration: 3,
                  ease: "easeInOut"
                }
              }}
              onClick={() => collectItem(item)}
            >
              {item.type}
            </CollectibleItem>
          ))}
        </AnimatePresence>
      </GameElementsContainer>
      
      <ScoreDisplay
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 1 }}
      >
        <span>⭐</span>
        <span>{score} points</span>
      </ScoreDisplay>
      
      {achievement && (
        <AchievementNotification
          initial={{ opacity: 0, x: 100 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: 100 }}
          transition={{ type: "spring", stiffness: 300, damping: 25 }}
        >
          <AchievementTitle>{achievement.title}</AchievementTitle>
          <AchievementDescription>{achievement.description}</AchievementDescription>
        </AchievementNotification>
      )}
    </>
  );
};

export default GameElements;
